import { css } from 'aphrodite';
import React from 'react';
import { styleSheet } from './styles';

const GENERATING_CONTENT_MESSAGES = [
	'Good things take time… great things take a few more seconds.',
	'Just adding the final sprinkle of magic dust ✨.',
	'We’re stitching your content together — no duct tape required.',
	'Your content is stretching and warming up… almost showtime!',
	'Your content is in the oven. Smells like engagement.',
	'Sharpening pencils, aligning pixels, perfecting posts.',
	'Content this fresh needs a moment to ripen.',
	'Your brand’s voice is clearing its throat 🎤.',
	'Herding hashtags into neat little rows…',
];

const GENERATING_IMAGE_MESSAGES = [
	'Mixing your brand colors…',
	'Repainting this one in your palette.',
	'Finding the right spot for your logo.',
	'Giving this picture your house style.',
	'Matching the type to your brand.',
	'Touching up the details ✨.',
	'Redrawing this one in your colors.',
];

const MESSAGE_ROTATION_INTERVAL = 4000;
const STAR_GENERATION_INTERVAL = 200;
const TILE_STAR_GENERATION_INTERVAL = 500;

type GeneratingContentVariant = 'panel' | 'tile';

export const GeneratingContentLoader = ({
	isGeneratingContent,
	className = '',
	message,
	variant = 'panel',
}: {
	isGeneratingContent: boolean;
	className?: string;
	message?: string;
	variant?: GeneratingContentVariant;
}) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const messages = variant === 'tile' ? GENERATING_IMAGE_MESSAGES : GENERATING_CONTENT_MESSAGES;
	const [messageIndex, setMessageIndex] = React.useState(Math.floor(Math.random() * messages.length));

	React.useEffect(() => {
		const interval = setInterval(() => {
			setMessageIndex(Math.floor(Math.random() * messages.length));
		}, MESSAGE_ROTATION_INTERVAL);
		return () => clearInterval(interval);
	}, [messages.length]);

	const addStar = React.useCallback(() => {
		if (!ref.current) {
			return;
		}
		const star = document.createElement('div');
		star.className = 'star';
		star.style.setProperty(
			'--size',
			variant === 'tile' ? Math.random() * 24 + 12 + '%' : Math.random() * 10 + 3 + 'vmin'
		);
		star.style.left = Math.floor(Math.random() * 100) + '%';
		star.style.top = Math.floor(Math.random() * 100) + '%';
		star.onanimationend = () => {
			star.remove();
		};
		ref.current.appendChild(star);
	}, [variant]);

	React.useEffect(() => {
		if (!isGeneratingContent) {
			return;
		}

		const interval = setInterval(
			() => {
				addStar();
			},
			variant === 'tile' ? TILE_STAR_GENERATION_INTERVAL : STAR_GENERATION_INTERVAL
		);
		return () => clearInterval(interval);
	}, [addStar, isGeneratingContent, variant]);

	return (
		<div
			data-testid='generating-content'
			ref={ref}
			className={`${css(
				styleSheet.generatingContent,
				variant === 'tile' ? styleSheet.generatingContentTile : null,
				isGeneratingContent ? styleSheet.generatingContentActive : null
			)} ${className}`}
		>
			<p className={css(variant === 'tile' ? styleSheet.generatingContentTileMessage : null)}>
				{message ?? messages[messageIndex]}
			</p>
		</div>
	);
};
